import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

function CloneElectionModal({ show, onHide, onConfirm, originalTitle, loading }) {
    const { t } = useTranslation(['modal', 'common']);
    const [newTitle, setNewTitle] = useState('');
    const [isValid, setIsValid] = useState(false);

    // Initialize title when modal opens
    useEffect(() => {
        if (show && originalTitle) {
            const clonedTitle = `${originalTitle}`;
            setNewTitle(clonedTitle);
            setIsValid(clonedTitle.trim().length > 0);
        }
    }, [show, originalTitle]);

    // Validate title input
    useEffect(() => {
        setIsValid(newTitle.trim().length > 0);
    }, [newTitle]);

    const handleTitleChange = (e) => {
        setNewTitle(e.target.value);
    };

    const handleConfirm = () => {
        if (isValid && !loading) {
            onConfirm(newTitle.trim());
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Escape') {
            onHide();
        } else if (e.key === 'Enter' && isValid && !loading) {
            handleConfirm();
        }
    };

    const handleCancel = () => {
        setNewTitle('');
        onHide();
    };

    if (!show) return null;

    return (
        <div 
            className="modal show d-block" 
            tabIndex="-1" 
            style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
            onKeyDown={handleKeyDown}
        >
            <div className="modal-dialog modal-dialog-centered">
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title">{t('modal:cloneElection.title')}</h5>
                        <button 
                            type="button" 
                            className="btn-close" 
                            onClick={handleCancel}
                            disabled={loading}
                        ></button>
                    </div>
                    <div className="modal-body">
                        <div className="alert alert-info">
                            <strong>{t('modal:cloneElection.alert.title')}</strong>
                        </div>
                        
                        <p dangerouslySetInnerHTML={{ __html: t('modal:cloneElection.description', { originalTitle }) }} />

                        <div className="mb-3">
                            <label htmlFor="cloneTitle" className="form-label">
                                <strong>{t('modal:cloneElection.form.titleLabel')}</strong>
                            </label>
                            <input
                                type="text"
                                id="cloneTitle"
                                className={`form-control ${!isValid && newTitle.length > 0 ? 'is-invalid' : ''}`}
                                value={newTitle}
                                onChange={handleTitleChange}
                                placeholder={t('modal:cloneElection.form.titlePlaceholder')}
                                disabled={loading}
                                maxLength={255}
                                autoFocus
                            />
                            {!isValid && newTitle.length > 0 && (
                                <div className="invalid-feedback">
                                    {t('modal:cloneElection.form.titleError')}
                                </div>
                            )}
                            <div className="form-text">
                                {t('modal:cloneElection.form.titleHelp')}
                            </div>
                        </div>

                        <div className="clone-info bg-light p-3 rounded">
                            <div>
                                <strong>{t('modal:cloneElection.cloneInfo.willBeCloned')}</strong>
                            </div>
                            <div className="mt-2">
                                • {t('modal:cloneElection.cloneInfo.items.title')}
                                <br />
                                • {t('modal:cloneElection.cloneInfo.items.description')}
                                <br />
                                • {t('modal:cloneElection.cloneInfo.items.options')}
                                <br />
                                • {t('modal:cloneElection.cloneInfo.items.settings')}
                            </div>
                            <div className="mt-2">
                                <strong>{t('modal:cloneElection.cloneInfo.willNotBeCloned')}</strong>
                            </div>
                            <div className="mt-1">
                                • {t('modal:cloneElection.cloneInfo.notItems.votes')}
                                <br />
                                • {t('modal:cloneElection.cloneInfo.notItems.adminAccess')}
                            </div>
                        </div>
                    </div>
                    <div className="modal-footer">
                        <button 
                            type="button" 
                            className="btn btn-outline-secondary" 
                            onClick={handleCancel}
                            disabled={loading}
                        >
                            {t('modal:cloneElection.buttons.cancel')}
                        </button>
                        <button 
                            type="button" 
                            className="btn btn-primary" 
                            onClick={handleConfirm}
                            disabled={loading || !isValid}
                        >
                            {loading ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                    {t('modal:cloneElection.buttons.creating')}
                                </>
                            ) : (
                                t('modal:cloneElection.buttons.create')
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default CloneElectionModal;